import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent,
} from "react";

/** Secondary controls stay visible while a user is operating them. */
export function useMonitorControls(active: boolean, dialogOpen: boolean) {
  const [visible, setVisible] = useState(true);
  const focused = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTimer = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const restartTimer = useCallback(() => {
    clearTimer();
    if (!active || dialogOpen || focused.current) return;
    timer.current = setTimeout(() => setVisible(false), 5000);
  }, [active, dialogOpen, clearTimer]);

  useEffect(() => {
    if (!active) focused.current = false;
    setVisible(true);
    restartTimer();
    return clearTimer;
  }, [active, restartTimer, clearTimer]);

  const onFocusCapture = useCallback(() => {
    focused.current = true;
    clearTimer();
    setVisible(true);
  }, [clearTimer]);
  const onBlurCapture = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (
        event.relatedTarget &&
        event.currentTarget.contains(event.relatedTarget as Node)
      )
        return;
      focused.current = false;
      restartTimer();
    },
    [restartTimer],
  );
  const onScreenTap = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const target = event.target as HTMLElement;
      if (
        target.closest("button, a, input, textarea, select, dialog") ||
        dialogOpen ||
        focused.current
      ) {
        setVisible(true);
        restartTimer();
        return;
      }
      setVisible((current) => !current);
      restartTimer();
    },
    [dialogOpen, restartTimer],
  );

  return {
    controlsVisible: visible,
    onFocusCapture,
    onBlurCapture,
    onScreenTap,
  };
}
